/**
 * Turning a Roboflow class name into a routine-variable key, and back to a
 * readable label.
 *
 * One module because the two halves must agree: the Train step derives an
 * `InspectClass.id` from the model's raw class name, and the runner publishes
 * `count_<id>` from that same id. A routine's saved `If` condition stores the
 * resulting key, so a change here breaks conditions operators already wrote —
 * treat these functions as a persisted format, not a formatting helper.
 */

/**
 * Characters a routine-variable key can hold. `InspectClass.id` is constrained
 * to `/^\w+$/` by its schema, and this is the same class expressed as its
 * complement, for collapsing everything else.
 */
const NON_KEY_CHARS = /[^\w]+/g;

/** Prefix every per-class count variable carries. */
const COUNT_PREFIX = 'count_';

/**
 * The id for one Roboflow class name.
 *
 * Runs of disallowed characters collapse to a single underscore, and leading or
 * trailing ones are dropped — so `Reject - Wrinkle` gives `Reject_Wrinkle`
 * rather than `Reject___Wrinkle`. Case is preserved, because the id is what the
 * operator reads in the If picker and `finesse_wrinkle` is worse than
 * `Finesse_wrinkle` there.
 *
 * Derived from the label alone, never from the class's position in the model's
 * list. A positional component would survive a rename but silently repoint a
 * saved condition at a different defect when a model version reorders its
 * classes — the same hazard that ruled out using the integer `class_id`.
 *
 * Returns an empty string for a label with no usable characters at all (`"---"`,
 * `"   "`). That cannot be a valid id, so a caller must treat it as a rejection
 * rather than saving it — see `findInspectClassIdConflicts`.
 */
export function sanitizeInspectClassLabel(label: string): string {
  return label.replace(NON_KEY_CHARS, '_').replace(/^_+|_+$/g, '');
}

/**
 * The routine-variable key a class's detection count is published under.
 *
 * The return type is the template literal and not a bare `string`, because the
 * Inspect step's `Variables` type keys its counts as `count_${string}` so that
 * they stay out of the structural narrowing its docblock explains. A `string`
 * here would force a cast at every write, and a cast is exactly what would let
 * the two drift apart without an error.
 */
export function inspectCountVariableKey(id: string): `count_${string}` {
  return `${COUNT_PREFIX}${id}`;
}

/** One reason a set of class names cannot be turned into keys. */
export type InspectClassIdConflict =
  | {
      kind: 'unusable';
      /**
       * Every label that sanitizes to nothing, not one at a time. Reported
       * together because the operator's fix is a rename in Roboflow followed by
       * a re-import: naming one at a time means two bad names cost two rounds
       * of that, while the copy is already plural.
       */
      labels: string[];
    }
  | {
      kind: 'collision';
      /** The id two or more labels both sanitize to. */
      id: string;
      /** Every label that produced it, in the order given. */
      labels: string[];
    };

/**
 * Why a model's class names cannot all become distinct keys.
 *
 * Two labels can sanitize to one id — `J clip` and `J-clip` both give `J_clip` —
 * and the collision is only visible across the whole set, which is why this
 * takes all the labels at once rather than validating one at a time.
 *
 * A conflict is reported rather than resolved. A counter suffix would make the
 * id depend on ordering, so a model version that reordered its classes could
 * repoint a saved condition at a different defect; and silently dropping one
 * class would hide a defect type the model can emit. Naming both labels lets
 * the operator rename one in Roboflow, which is the only fix that leaves the
 * keys stable.
 *
 * Empty when every label yields a distinct, usable id. No such conflict exists
 * anywhere in our own workspace today, so this guards a case real data has not
 * yet produced.
 */
export function findInspectClassIdConflicts(
  labels: readonly string[],
): InspectClassIdConflict[] {
  const conflicts: InspectClassIdConflict[] = [];
  const byId = new Map<string, string[]>();
  const unusable: string[] = [];

  for (const label of labels) {
    const id = sanitizeInspectClassLabel(label);

    if (!id) {
      unusable.push(label);
      continue;
    }

    byId.set(id, [...(byId.get(id) ?? []), label]);
  }

  // One conflict for all of them, so a single re-import fixes every bad name.
  if (unusable.length > 0) {
    conflicts.push({ kind: 'unusable', labels: unusable });
  }

  for (const [id, sharing] of byId) {
    if (sharing.length > 1) {
      conflicts.push({ kind: 'collision', id, labels: sharing });
    }
  }

  return conflicts;
}
